import React, { useEffect, useRef, useState } from "react";
import { pushLayer } from "../utils/layers";
import { onSwipe } from "../utils/gestures";
import { useIsMobile } from "../utils/viewport";
import {
  Gauge, Bot, Briefcase, Sparkles, FileSpreadsheet, ClipboardList,
  ShieldCheck, Landmark, LogOut, X, ChevronsLeft, ChevronsRight, Sun, Moon, Crown, Rocket, ScanSearch, GitCompareArrows, Building2, BookOpen, LifeBuoy,
  Send, Coins,
} from "lucide-react";
import { LogoMark, LogoWordmark } from "./Logo";
import { AppUser } from "../types";

type ViewKey =
  | "main" | "admin" | "admindocs" | "cabinet" | "org" | "builder" | "reports" | "clients"
  | "agent" | "banks" | "credit" | "bankmethods" | "broker" | "platform" | "experience" | "premium" | "about" | "tenders" | "product_compare" | "help_admin" | "help"
  | "applications";

interface NavItem {
  view: ViewKey; label: string; sub: string;
  Icon: React.ComponentType<{ className?: string }>;
  premium?: boolean;
  /** Кому пункт виден. Не задано — видят все авторизованные. */
  visible?: (u: AppUser) => boolean;
}

// Меню собрано по смыслу работы, а не по уровню доступа: что делаю с клиентом, чем
// настраиваю систему, чем управляю. Доступ остался прежним и задаётся полем `visible`
// у пункта — перенос раздела не должен открывать никому лишнего.
type Visibility = (u: AppUser) => boolean;
const isAdmin: Visibility = (u) => !!u.is_admin;
// Кабинет площадки виден оператору и владельцу организации-площадки. Роль назначает
// администратор: сам пользователь её себе не выдаёт (#591).
const isPlatformOperator: Visibility = (u) => u.org_role === "platform_operator";
// Рыночная аналитика (сравнение продуктов) — пакет ИЛИ роль оператора площадки. Сам пакет
// оператору не выдаётся: он открывает ещё и редакторы реестра банков и кредитов (#594).
const seesMarket: Visibility = (u) => hasPkg("bank_analytics")(u) || isPlatformOperator(u);
const hasPkg = (pkg: string): Visibility => (u) => !!u.is_admin || !!u.packages?.includes(pkg);

const PRIMARY: NavItem[] = [
  { view: "broker", label: "Скоринг", sub: "Подбор банков", Icon: Briefcase },
  { view: "main", label: "Анализ", sub: "Экспресс-оценка", Icon: Gauge },
  { view: "agent", label: "AI агент", sub: "Диалоговый анализ", Icon: Bot },
];

// Мои клиенты — то, что оператор ведёт по конкретному контрагенту.
const CLIENTS: NavItem[] = [
  // Банки СВОЕЙ площадки: их условия оператор ведёт сам, чужие ему не видны.
  { view: "platform", label: "Моя площадка", sub: "Банки, тарифы и условия",
    Icon: Building2, visible: isPlatformOperator },
  // Один вход вместо двух: отчётность и опыт живут внутри карточки клиента, где ИНН
  // уже выбран. Раньше это были отдельные экраны с ручным вводом ИНН на каждом.
  { view: "clients", label: "Мои клиенты", sub: "Расчёты · отчётность · опыт",
    Icon: ClipboardList, premium: true },
  // Отправленные в СНС заявки: решения банков, допзапросы, уведомления.
  { view: "applications", label: "Заявки", sub: "Отправленные в СНС", Icon: Send },
];

// Инструменты — настройка того, ПО ЧЕМУ считаем и с чем сравниваем.
const TOOLS: NavItem[] = [
  { view: "builder", label: "Методики", sub: "Конструктор", Icon: Sparkles, premium: true },
  { view: "banks", label: "Банки (БГ)", sub: "Тарифы и требования", Icon: Landmark,
    visible: hasPkg("bank_analytics") },
  { view: "credit", label: "Кредиты", sub: "Кредиты, займы, лизинг", Icon: Coins,
    visible: hasPkg("bank_analytics") },
  { view: "bankmethods", label: "Банковские методики", sub: "Настройка и «Банк N»", Icon: Landmark,
    visible: isAdmin },
  { view: "product_compare", label: "Сравнение продуктов", sub: "Маркетинг-анализ карточек",
    Icon: GitCompareArrows, visible: seesMarket },
  { view: "tenders", label: "Тендераналитика", sub: "Прогноз победителей + ИИ-аудит",
    Icon: ScanSearch, visible: hasPkg("tenders") },
];

// Администрирование — управление доступом и содержимым сервиса. Карточка организации
// доступна всем: это реквизиты, сотрудники и ключ СВОЕЙ организации, а не админка.
const ADMIN: NavItem[] = [
  { view: "org", label: "Организация", sub: "Карточка · люди · ключ", Icon: Building2 },
  { view: "admin", label: "Админка", sub: "Пользователи", Icon: ShieldCheck, visible: isAdmin },
  { view: "admindocs", label: "Документация", sub: "Руководство админа", Icon: BookOpen, visible: isAdmin },
  { view: "help_admin", label: "Справка", sub: "Редактор статей «?»", Icon: LifeBuoy, visible: isAdmin },
];

interface Props {
  currentUser: AppUser;
  view: ViewKey;
  onNavigate: (v: ViewKey) => void;
  onLogout: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
  dark: boolean;
  onToggleTheme: () => void;
  taskCount?: number;
  /** Непрочитанные события по заявкам СНС; attention — среди них требующие действий. */
  applications?: { count: number; attention: number };
}

const COLLAPSE_KEY = "anyskin.sidebar.collapsed";

export default function Sidebar({ currentUser, view, onNavigate, onLogout, mobileOpen, onMobileClose, dark, onToggleTheme, taskCount = 0, applications }: Props) {
  // По умолчанию меню — колонка иконок (новый дизайн); развёрнутое — если пользователь
  // сам его развернул (выбор запоминается).
  const [collapsed, setCollapsed] = useState<boolean>(
    () => typeof window === "undefined" || localStorage.getItem(COLLAPSE_KEY) !== "0");

  // Esc закрывает мобильный drawer.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onMobileClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen, onMobileClose]);

  // Экран перешёл в режим работы с результатом (успешный подбор в Скоринге) — сворачиваем
  // меню, чтобы широкая колонка навигации не отъедала место у выдачи по банкам.
  // Выбор пользователя в localStorage НЕ трогаем: это сворачивание по случаю, а не смена
  // настройки — перезагрузил страницу, и меню снова такое, каким он его оставил. Развернуть
  // обратно можно сразу же, повторно мы не сворачиваем (событие шлётся только на расчёте).
  useEffect(() => {
    const onCollapse = () => setCollapsed(true);
    window.addEventListener("anyskin:rail-collapse", onCollapse);
    return () => window.removeEventListener("anyskin:rail-collapse", onCollapse);
  }, []);

  const toggleCollapse = () => {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      return next;
    });
  };

  const go = (v: ViewKey) => { onNavigate(v); onMobileClose(); };
  const premium = currentUser.is_premium || currentUser.is_admin;
  const hideText = collapsed ? "md:hidden" : "";

  const NavBtn = ({ item }: { item: NavItem }) => {
    const active = view === item.view;
    const I = item.Icon;
    return (
      <button
        onClick={() => go(item.view)}
        title={collapsed ? item.label : ""}
        aria-label={item.label}
        aria-current={active ? "page" : undefined}
        className={`group relative w-full flex items-center gap-3 rounded-full px-3 py-2.5 text-left transition-colors cursor-pointer ${
          active ? "bg-indigo-50 text-indigo-700 md:bg-indigo-600/10" : "text-slate-600 hover:bg-slate-50 md:hover:bg-black/5 dark:md:hover:bg-white/5 hover:text-slate-900"
        } ${collapsed ? "md:justify-center md:px-0 md:w-11 md:h-11 md:mx-auto" : ""}`}
      >
        {item.view === "broker" && taskCount > 0 && (
          <span className="absolute top-1 right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold inline-flex items-center justify-center" title={`Задачи подбора: ${taskCount}`}>{taskCount}</span>
        )}
        {item.view === "applications" && (applications?.count || 0) > 0 && (
          <span className={`absolute top-1 right-1.5 min-w-[16px] h-4 px-1 rounded-full text-white text-[9px] font-bold inline-flex items-center justify-center ${applications!.attention > 0 ? "bg-rose-500" : "bg-indigo-500"}`}
            title={`Новые события по заявкам: ${applications!.count}${applications!.attention ? `, требуют действий: ${applications!.attention}` : ""}`}>{applications!.count}</span>
        )}
        <I className={`w-[18px] h-[18px] shrink-0 ${active ? "text-indigo-600" : "text-slate-400 group-hover:text-slate-600"}`} />
        <span className={`min-w-0 ${hideText}`}>
          <span className="flex items-center gap-1 text-sm font-semibold leading-tight truncate">
            {item.label}
            {item.premium && !premium && <Crown className="w-3 h-3 text-amber-500 shrink-0" />}
          </span>
          <span className="block text-[11px] md:text-[10px] text-slate-500 leading-tight truncate">{item.sub}</span>
        </span>
      </button>
    );
  };

  const GroupLabel = ({ title }: { title: string }) => (
    <>
      <div className={`px-3 pt-4 pb-1 t-meta uppercase tracking-wider text-slate-400 font-bold ${hideText}`}>{title}</div>
      {collapsed && <div className="hidden md:block mx-auto w-6 my-2 border-t border-slate-200" aria-hidden />}
    </>
  );

  /** Группа пунктов: заголовок рисуется, только если пользователю доступен хотя бы
   *  один пункт — пустых разделов в меню быть не должно. */
  const NavGroup = ({ title, items }: { title: string; items: NavItem[] }) => {
    const shown = items.filter((it) => !it.visible || it.visible(currentUser));
    if (!shown.length) return null;
    return (
      <>
        <GroupLabel title={title} />
        {shown.map((it) => <NavBtn key={it.view} item={it} />)}
      </>
    );
  };

  const initial = (currentUser.username || "?").trim().charAt(0).toUpperCase();

  // Меню на телефоне — окно (мобильный аудит, M-43): «Назад» и Esc закрывают его, фон не
  // прокручивается, фокус — на текущем разделе.
  const mobile = useIsMobile();
  const asideRef = useRef<HTMLElement | null>(null);
  const closeRef = useRef(onMobileClose);
  closeRef.current = onMobileClose;
  useEffect(() => {
    if (!(mobile && mobileOpen)) return;
    const pop = pushLayer(() => closeRef.current());
    requestAnimationFrame(() => asideRef.current?.querySelector<HTMLElement>('[aria-current="page"]')?.focus({ preventScroll: true }));
    // На весь экран, а не только на панель: справа от неё затемнение, и свайп влево по нему —
    // тоже «закрыть меню».
    const unswipe = onSwipe(document.documentElement, "left", () => closeRef.current());
    return () => { pop(); unswipe(); };
  }, [mobile, mobileOpen]);

  return (
    <>
      {/* Затемнение под мобильным drawer */}
      {mobileOpen && (
        <div onClick={onMobileClose} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-(--z-backdrop) md:hidden" aria-hidden />
      )}

      <aside ref={asideRef}
        {...(mobile ? { role: "dialog", "aria-modal": mobileOpen, "aria-label": "Меню", "aria-hidden": !mobileOpen } : {})}
        className={`fixed md:sticky top-0 left-0 z-(--z-sheet) md:z-(--z-rail) h-dvh flex flex-col bg-white border-r border-slate-100 md:bg-transparent md:border-0 app-rail shadow-2xl md:shadow-none transition-[transform,width] duration-200 ease-out w-72 ${
          collapsed ? "md:w-[68px]" : "md:w-64"
        } ${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
      >
        {/* Бренд */}
        <div className={`flex items-center gap-2.5 h-16 px-3 shrink-0 border-b border-slate-100 md:border-0 ${collapsed ? "md:justify-center md:px-0" : ""}`}>
          <button onClick={() => go("broker")} className="flex items-center gap-2.5 min-w-0 min-h-11 cursor-pointer" title="SNSanyskin — скоринг и подбор">
            {/* Свёрнутое меню — знак, развёрнутое — логотип SNSanyskin. */}
            <LogoMark size={34} className={`shrink-0 drop-shadow-sm ${collapsed ? "hidden md:block" : "hidden"}`} />
            <span className={`min-w-0 text-left ${hideText}`}>
              <span className="flex items-center gap-1.5">
                <LogoWordmark className="h-6" />
              </span>
              <span className="block text-[9px] text-slate-400 leading-tight mt-0.5 truncate">Экспресс-анализ контрагентов</span>
            </span>
          </button>
          <button onClick={onMobileClose} className="md:hidden ml-auto grid place-items-center w-11 h-11 shrink-0 -mr-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 rounded-lg cursor-pointer" title="Закрыть меню" aria-label="Закрыть меню">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Навигация */}
        <nav className={`flex-1 overflow-y-auto py-3 space-y-0.5 ${collapsed ? "px-2.5 md:px-0" : "px-2.5"}`}>
          {PRIMARY.map((it) => <NavBtn key={it.view} item={it} />)}
          {/* Группа показывается, только если пользователю виден хотя бы один её пункт —
              иначе в меню оставались бы пустые заголовки. Премиум-пункты видны всем,
              без премиума — с короной (клик ведёт на промо). */}
          <NavGroup title="Мои клиенты" items={CLIENTS} />
          <NavGroup title="Инструменты" items={TOOLS} />
          <NavGroup title="Администрирование" items={ADMIN} />
          <GroupLabel title="О сервисе" />
          <NavBtn item={{ view: "help", label: "Справочный центр", sub: "Инструкции и туры", Icon: LifeBuoy }} />
          <NavBtn item={{ view: "about", label: "О проекте", sub: "Возможности и кому подходит", Icon: Rocket }} />
        </nav>

        {/* Пользователь + сворачивание */}
        <div className={`mt-auto border-t border-slate-100 md:border-0 p-2.5 ${collapsed ? "md:px-1.5" : ""}`}>
          <div className="flex items-center gap-1.5">
            <button onClick={() => go("cabinet")}
              className={`flex items-center gap-2.5 min-w-0 flex-1 rounded-full px-2 py-2 hover:bg-slate-50 md:hover:bg-black/5 dark:md:hover:bg-white/5 transition-colors cursor-pointer ${collapsed ? "md:justify-center md:px-0" : ""}`}
              title="Личный кабинет">
              <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center text-sm shrink-0">{initial}</span>
              <span className={`min-w-0 text-left ${hideText}`}>
                <span className="block text-sm font-semibold text-slate-800 leading-tight truncate">{currentUser.username}</span>
                <span className="block text-[10px] text-slate-400 leading-tight truncate">
                  {currentUser.is_admin ? "🛡 Администратор" : (currentUser.email || "Личный кабинет")}
                </span>
              </span>
            </button>
            <button onClick={onLogout}
              className={`grid place-items-center w-11 h-11 md:w-auto md:h-auto md:p-2 rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors cursor-pointer ${collapsed ? "md:hidden" : ""}`}
              title="Выйти" aria-label="Выйти">
              <LogOut className="w-[18px] h-[18px]" />
            </button>
          </div>
          <div className="mt-1.5 flex items-center gap-1.5">
            <button onClick={onToggleTheme}
              className={`flex flex-1 items-center justify-center gap-1.5 min-h-11 md:min-h-0 py-1.5 rounded-lg text-xs md:text-[11px] font-semibold text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors cursor-pointer ${collapsed ? "md:px-0" : ""}`}
              title={dark ? "Светлая тема" : "Тёмная тема"}>
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              <span className={hideText}>{dark ? "Светлая" : "Тёмная"}</span>
            </button>
            <button onClick={toggleCollapse}
              className="hidden md:flex items-center justify-center p-1.5 rounded-lg text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors cursor-pointer shrink-0"
              title={collapsed ? "Развернуть меню" : "Свернуть меню"}>
              {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
